import { Box, BoxProps, forwardRef } from '@chakra-ui/react';
import { createContext, useContext } from 'react';

import { ActionButtonProps, ActionButtons } from './action-buttons';

/** When true, cell-level hover groups are disabled and row-level hover controls visibility. */
export const TableRowFloatingButtonsContext = createContext(false);

interface FloatingActionButtonsProps extends ActionButtonProps {
  reserveSpace?: boolean;
}

export const FloatingActionButtons = forwardRef<FloatingActionButtonsProps, 'button'>(
  function FloatingActionButtonsComponent(props, ref) {
    const { reserveSpace, ...actionButtonProps } = props;
    const { showButtons } = useFloatingButtonsContext();
    const isInTableRow = useContext(TableRowFloatingButtonsContext);
    const hiddenStyles = reserveSpace
      ? { visibility: 'hidden' as const, pointerEvents: 'none' as const }
      : { display: 'none' as const };
    const groupHoverStyles = reserveSpace
      ? { visibility: 'visible' as const, pointerEvents: 'auto' as const }
      : { display: 'flex' as const };

    return (
      <ActionButtons
        {...(isInTableRow && { ml: 'auto', flexShrink: 0 })}
        {...actionButtonProps}
        disableTooltips={actionButtonProps.disableTooltips || (isInTableRow && !showButtons)}
        {...(!showButtons && hiddenStyles)}
        _groupHover={groupHoverStyles}
        ref={ref}
      />
    );
  },
);

type FloatingButtonsContainerProps = {
  /* This prop can override internal hide/show logic of the Floating component */
  showButtons?: boolean;
} & BoxProps;
export const FloatingButtonsContainer = forwardRef<FloatingButtonsContainerProps, 'div'>(
  ({ showButtons, ...restProps }, ref) => {
    return (
      <Context.Provider value={{ showButtons }}>
        <Box ref={ref} {...restProps} data-group />
      </Context.Provider>
    );
  },
);

const Context = createContext<{ showButtons?: boolean } | null>(null);

function useFloatingButtonsContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'FloatingActionButtons component must be wrapped in <FloatingButtonsContainer />',
    );
  }

  return context;
}
